<template>
  <div class="home-page">
    <!-- Decorative floating sprites (Unicode + CSS, no extra images) -->
    <div class="home-stars" aria-hidden="true">
      <span class="star star--1">✦</span>
      <span class="star star--2">✦</span>
      <span class="star star--3">✧</span>
      <span class="star star--4">✦</span>
      <span class="star star--5">✧</span>
      <span class="star star--6">✦</span>
    </div>
    <div class="home-sprites" aria-hidden="true">
      <span class="sprite sprite--mushroom-1">🍄</span>
      <span class="sprite sprite--leaf-1">🍂</span>
      <span class="sprite sprite--flower-1">🌼</span>
      <span class="sprite sprite--leaf-2">🍃</span>
      <span class="sprite sprite--mushroom-2">🍄</span>
      <span class="sprite sprite--flower-2">🌸</span>
      <span class="sprite sprite--sparkle-1">✨</span>
      <span class="sprite sprite--sparkle-2">✨</span>
    </div>

    <!-- Premium Header -->
    <header class="home-header">
      <div class="home-header__left">
        <img
          src="../assets/images/logo2.png"
          loading="lazy"
          class="home-header__logo mascot-float"
          alt="UFA Easy888"
        />
      </div>
      <div class="home-header__center">
        <h1 class="home-header__title">UFA Easy888</h1>
        <span class="home-header__code">{{ code }}</span>
      </div>
      <div class="home-header__right">
        <div
          class="home-header__avatar"
          :style="{ backgroundImage: `url(${pictureUrl})` }"
        ></div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="home-content">
      <!-- Welcome Card with mascot illustration -->
      <section class="welcome-card welcome-card--hybrid animate-fade-in-up">
        <div class="welcome-card__glow"></div>
        <div class="welcome-card__content">
          <span class="welcome-card__label">ยินดีต้อนรับ</span>
          <p class="welcome-card__message">พร้อมให้บริการ 24 ชม.</p>
          <span class="welcome-card__hint">ลุยกันเลย!</span>
        </div>
        <!-- Mascot character (AI-gen chibi gryphon) -->
        <div class="welcome-card__mascot">
          <img
            src="../assets/images/ai/mascot-gryphon.png"
            alt="mascot"
            class="mascot-float"
          />
          <span class="mascot-shadow"></span>
        </div>
      </section>

      <!-- Quick Actions -->
      <section class="quick-actions">
        <button
          class="action-btn action-btn--deposit"
          @click="goTo('/deposit')"
        >
          <div class="action-btn__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 4V20M4 12H20"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <span class="action-btn__label">ฝากเงิน</span>
        </button>

        <button
          class="action-btn action-btn--withdraw"
          @click="goTo('/withdraw')"
        >
          <div class="action-btn__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 12H20"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <span class="action-btn__label">ถอนเงิน</span>
        </button>

        <button class="action-btn action-btn--play" @click="goTo('/playlogin')">
          <div class="action-btn__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
            </svg>
          </div>
          <span class="action-btn__label">เข้าเล่น</span>
        </button>

        <button
          class="action-btn action-btn--history"
          @click="goTo('/history')"
        >
          <div class="action-btn__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <span class="action-btn__label">ประวัติ</span>
        </button>
      </section>

      <!-- VIP Golden Spin: Aug 5,000+ depositors (7–10 Sep 2026) -->
      <button
        v-if="showVipSpin"
        class="promo-banner promo-banner--vipspin animate-fade-in-up"
        @click="goTo('/vip-spin')"
      >
        <span class="promo-banner__emoji">🎰</span>
        <span class="promo-banner__text">
          <strong>VIP Golden Spin ลุ้น 100–300</strong>
          <small>ลูกค้ายอดฝาก ส.ค. 5,000+ • หมุนฟรี 7–10 ก.ย.</small>
        </span>
        <span class="promo-banner__arrow">›</span>
      </button>

      <!-- Miss You promo: ฝาก 10 รับ 100 (5–10 Sep 2026) -->
      <button
        v-if="showMissYouPromo"
        class="promo-banner promo-banner--missyou animate-fade-in-up"
        @click="goTo('/miss-you')"
      >
        <span class="promo-banner__emoji">💛</span>
        <span class="promo-banner__text">
          <strong>โปรคิดถึงนะ ฝาก 10 รับ 100</strong>
          <small>ลูกค้าที่ยังไม่ได้ฝากเดือนนี้ • รับได้ 5–10 ก.ย.</small>
        </span>
        <span class="promo-banner__arrow">›</span>
      </button>

      <!-- Share Friend Button -->
      <button
        class="share-btn btn-chunky animate-pulse-gold"
        @click="shareFriend()"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.17209C7.54305 8.46329 6.66255 8 5.66667 8C4.19391 8 3 9.34315 3 11C3 12.6569 4.19391 14 5.66667 14C6.66255 14 7.54305 13.5367 8.08261 12.8279L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C17.0041 14 16.1236 14.4633 15.5841 15.1721L8.64396 11.3706C8.65895 11.2492 8.66667 11.1255 8.66667 11C8.66667 10.8745 8.65895 10.7508 8.64396 10.6294L15.5841 6.82791C16.1236 7.53671 17.0041 8 18 8Z"
            fill="currentColor"
          />
        </svg>
        <span>แนะนำเพื่อน รับโบนัส</span>
      </button>

      <!-- Announcements -->
      <section class="announcements">
        <div class="section-header">
          <h2 class="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            ประกาศข่าวสาร
          </h2>
        </div>

        <div class="announcement-card">
          <ul class="announcement-list">
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>UFA Easy888 [เว็บตรงไม่ผ่านเอเย่นต์]</span>
            </li>
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>เว็บกีฬาออนไลน์ ที่มาแรงที่สุดในตอนนี้</span>
            </li>
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>การเงินมั่นคงปลอดภัย 100%</span>
            </li>
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>ดูแลการเงินด้วยแอดมิน ตลอด 24 ชั่วโมง</span>
            </li>
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>บอล หวย มวย คาสิโน เกมส์ ครบวงจร</span>
            </li>
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>ระบบแจ้งฝากและถอนได้รวดเร็วที่สุด</span>
            </li>
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>ระบบเราสามารถดู History ย้อนหลังได้</span>
            </li>
            <li class="announcement-item">
              <span class="announcement-icon">🔥</span>
              <span>ฝาก - ถอน ภายใน 30 วินาที</span>
            </li>
          </ul>
        </div>
      </section>
    </main>

    <BottomMenu></BottomMenu>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/database";
import router from "./../router";
import liffService from "@/services/liff";
export default {
  name: "Home",
  components: {
    BottomMenu: require("./BottomMenu").default
  },
  created() {
    // LIFF is already initialized by router guard
    this.loadUserData();
  },
  data() {
    return {
      menuVisible: false,
      pictureUrl: "",
      history: [],
      code: ""
    };
  },
  computed: {
    showVipSpin() {
      // Hide once the spin window (7–10 Sep 2026, Asia/Bangkok) has passed.
      return Date.now() <= Date.parse("2026-09-10T23:59:59+07:00");
    },
    showMissYouPromo() {
      // Hide once the claim window (5–10 Sep 2026, Asia/Bangkok) has passed.
      return Date.now() <= Date.parse("2026-09-10T23:59:59+07:00");
    }
  },
  methods: {
    goTo(path) {
      this.$router.push(path);
    },
    async shareFriend() {
      await window.liff.shareTargetPicker([
        {
          type: "flex",
          altText: "UFA Easy888",
          contents: {
            type: "bubble",
            hero: {
              type: "image",
              url: "https://line-ufaeasy2.web.app/images/line-header.jpg",
              size: "full",
              aspectRatio: "20:13",
              aspectMode: "cover",
              action: {
                type: "uri",
                uri: "https://lin.ee/9MgANfz"
              }
            },
            body: {
              type: "box",
              layout: "vertical",
              spacing: "md",
              contents: [
                {
                  type: "text",
                  text: "UFA Easy888",
                  wrap: true,
                  weight: "bold",
                  gravity: "center",
                  size: "xl"
                },
                {
                  type: "box",
                  layout: "baseline",
                  margin: "md",
                  contents: [
                    {
                      type: "icon",
                      size: "sm",
                      url:
                        "https://scdn.line-apps.com/n/channel_devcenter/img/fx/review_gold_star_28.png"
                    },
                    {
                      type: "icon",
                      size: "sm",
                      url:
                        "https://scdn.line-apps.com/n/channel_devcenter/img/fx/review_gold_star_28.png"
                    },
                    {
                      type: "icon",
                      size: "sm",
                      url:
                        "https://scdn.line-apps.com/n/channel_devcenter/img/fx/review_gold_star_28.png"
                    },
                    {
                      type: "icon",
                      size: "sm",
                      url:
                        "https://scdn.line-apps.com/n/channel_devcenter/img/fx/review_gold_star_28.png"
                    },
                    {
                      type: "icon",
                      size: "sm",
                      url:
                        "https://scdn.line-apps.com/n/channel_devcenter/img/fx/review_gold_star_28.png"
                    },
                    {
                      type: "text",
                      text: "5.0",
                      size: "sm",
                      color: "#999999",
                      margin: "md",
                      flex: 0
                    }
                  ]
                },
                {
                  type: "box",
                  layout: "vertical",
                  margin: "lg",
                  spacing: "sm",
                  contents: [
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 UFA Easy888 [เว็บตรงไม่ผ่านเอเย่นต์]",
                          wrap: true,
                          size: "sm",
                          color: "#666666",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 เว็บกีฬาออนไลน์ ที่มาแรงที่สุดในตอนนี้",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 การเงินมั่นคงปลอดภัย 100 %",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 ดูแลการเงินด้วยแอดมิน ตลอด 24 ชั่วโมง",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 บอล หวย มวย คาสิโน เกมส์ ครบวงจร",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 เราใช้ระบบใหม่และดีที่สุดตอนนี้",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 ระบบแจ้งฝากและถอนได้รวดเร็วที่สุด",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 ระบบเราสามารถดู History ย้อนหลังได้",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 สามารถเข้าเล่นได้เพียงกดปุ่มเดียว",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    },
                    {
                      type: "box",
                      layout: "baseline",
                      spacing: "sm",
                      contents: [
                        {
                          type: "text",
                          text: "🔥 ฝาก - ถอน ภายใน 30วินาที",
                          wrap: true,
                          color: "#666666",
                          size: "sm",
                          flex: 4
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            footer: {
              type: "box",
              layout: "vertical",
              contents: [
                {
                  type: "spacer",
                  size: "xs"
                },
                {
                  type: "button",
                  style: "primary",
                  color: "#a99955",
                  action: {
                    type: "uri",
                    label: "เพิ่มเพื่อน",
                    uri: "https://lin.ee/9MgANfz"
                  }
                }
              ]
            }
          }
        }
      ]);
    },
    async loadUserData() {
      // Get profile from centralized service (already fetched by router guard)
      const profile = liffService.profile;
      if (!profile) return;

      this.pictureUrl = profile.pictureUrl || "none";
      const userId = profile.userId;

      if (userId) {
        const users = (
          await firebase
            .database()
            .ref(`/users`)
            .orderByChild("userId")
            .equalTo(userId)
            .once("value")
        ).val();
        if (!users) {
          router.replace("/register");
        } else {
          let user = {};
          for (const x in users) {
            user = users[x];
          }
          this.code = user.code;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// =============================================================================
// HOME PAGE - PREMIUM DESIGN
// =============================================================================

.home-page {
  position: relative;
  min-height: 100vh;
  background: transparent; // let .app cozy backdrop show through
  padding-bottom: 80px;
  overflow: hidden;
}

// -----------------------------------------------------------------------------
// HYBRID DECORATIONS — Stars / Sparkles
// -----------------------------------------------------------------------------

.home-stars {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;

  .star {
    position: absolute;
    color: rgba(255, 180, 130, 0.7);
    text-shadow: 0 0 8px rgba(255, 107, 53, 0.6);
    font-size: 14px;
    animation: twinkle 3s ease-in-out infinite;
  }
  .star--1 {
    top: 8%;
    left: 12%;
    font-size: 12px;
    animation-delay: 0s;
    color: rgba(255, 200, 150, 0.8);
  }
  .star--2 {
    top: 14%;
    right: 18%;
    font-size: 18px;
    animation-delay: 0.7s;
    color: rgba(255, 145, 101, 0.75);
  }
  .star--3 {
    top: 38%;
    left: 6%;
    font-size: 10px;
    animation-delay: 1.4s;
    color: rgba(255, 220, 180, 0.6);
  }
  .star--4 {
    top: 52%;
    right: 8%;
    font-size: 16px;
    animation-delay: 0.4s;
    color: rgba(255, 107, 53, 0.7);
  }
  .star--5 {
    top: 72%;
    left: 22%;
    font-size: 11px;
    animation-delay: 1.8s;
    color: rgba(255, 200, 150, 0.7);
  }
  .star--6 {
    top: 85%;
    right: 24%;
    font-size: 14px;
    animation-delay: 1.1s;
    color: rgba(255, 145, 101, 0.8);
  }
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.8) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(15deg);
  }
}

// -----------------------------------------------------------------------------
// HYBRID DECORATIONS — Sprites (mushrooms, leaves, flowers, sparkles)
// -----------------------------------------------------------------------------

.home-sprites {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100vh;
  max-width: 420px;
  pointer-events: none;
  z-index: 5;

  .sprite {
    position: absolute;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6)) brightness(1.1);
    user-select: none;
    opacity: 0.92;
  }

  .sprite--mushroom-1 {
    bottom: 92px;
    left: 6px;
    font-size: 30px;
    animation: spriteBob 4.2s ease-in-out infinite;
  }
  .sprite--mushroom-2 {
    bottom: 100px;
    right: 8px;
    font-size: 26px;
    animation: spriteBob 5s ease-in-out infinite 0.6s;
  }
  .sprite--leaf-1 {
    top: 100px;
    right: 2px;
    font-size: 22px;
    animation: leafSway 6s ease-in-out infinite;
  }
  .sprite--leaf-2 {
    bottom: 160px;
    left: 8px;
    font-size: 22px;
    animation: leafSway 5.4s ease-in-out infinite 1.2s;
  }
  .sprite--flower-1 {
    top: 88px;
    left: 2px;
    font-size: 20px;
    animation: spriteBob 4.8s ease-in-out infinite 0.3s;
  }
  .sprite--flower-2 {
    bottom: 140px;
    right: 2px;
    font-size: 18px;
    animation: spriteBob 4.4s ease-in-out infinite 0.9s;
  }
  .sprite--sparkle-1 {
    top: 42%;
    right: 8px;
    font-size: 18px;
    animation: sparkleTw 2.4s ease-in-out infinite;
  }
  .sprite--sparkle-2 {
    top: 62%;
    left: 4px;
    font-size: 16px;
    animation: sparkleTw 2.8s ease-in-out infinite 0.8s;
  }
}

@keyframes spriteBob {
  0%,
  100% {
    transform: translateY(0) rotate(-3deg);
  }
  50% {
    transform: translateY(-4px) rotate(3deg);
  }
}

@keyframes leafSway {
  0%,
  100% {
    transform: rotate(-10deg) translateY(0);
    opacity: 0.85;
  }
  50% {
    transform: rotate(10deg) translateY(-3px);
    opacity: 1;
  }
}

@keyframes sparkleTw {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.7);
  }
  50% {
    opacity: 1;
    transform: scale(1.1) rotate(20deg);
  }
}

// Ensure all in-page content sits above stars
.home-header,
.home-content {
  position: relative;
  z-index: 1;
}

// -----------------------------------------------------------------------------
// HEADER
// -----------------------------------------------------------------------------

.home-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top, 0px));
  background: linear-gradient(180deg, #1e1e1e 0%, rgba(30, 30, 30, 0.98) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 107, 53, 0.2);

  &__left {
    flex-shrink: 0;
  }

  &__logo {
    height: 56px;
    width: auto;
  }

  &__center {
    flex: 1;
    text-align: center;
    padding: 0 8px;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #ff6b35;
    margin: 0;
    line-height: 1.2;
  }

  &__code {
    display: inline-block;
    margin-top: 4px;
    padding: 2px 12px;
    background: rgba(255, 107, 53, 0.15);
    border: 1px solid rgba(255, 107, 53, 0.3);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    color: #ff9165;
  }

  &__right {
    flex-shrink: 0;
  }

  &__avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    background-color: #2a2a2a;
    border: 2px solid rgba(255, 107, 53, 0.4);
    box-shadow: 0 0 15px rgba(255, 107, 53, 0.2);
  }
}

// -----------------------------------------------------------------------------
// MAIN CONTENT
// -----------------------------------------------------------------------------

.home-content {
  padding: 16px;
}

// -----------------------------------------------------------------------------
// WELCOME CARD
// -----------------------------------------------------------------------------

.welcome-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 24px 24px;
  margin-bottom: 22px;
  background: radial-gradient(
      ellipse at 80% 20%,
      rgba(255, 255, 255, 0.18),
      transparent 50%
    ),
    linear-gradient(135deg, #ff6b35 0%, #e55520 100%);
  border-radius: 22px;
  overflow: hidden;
  border: 2px solid rgba(255, 217, 196, 0.35);
  box-shadow: 0 8px 0 rgba(184, 63, 18, 0.5),
    0 14px 30px rgba(255, 107, 53, 0.35),
    inset 0 2px 0 rgba(255, 255, 255, 0.25);

  &__glow {
    position: absolute;
    top: -50%;
    right: -20%;
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.3) 0%,
      transparent 70%
    );
    pointer-events: none;
  }

  &__content {
    position: relative;
    z-index: 1;
  }

  &__label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 4px;
  }

  &__message {
    font-family: "Mali", "Kanit", sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #0d0d0d;
    margin: 0;
    line-height: 1.15;
    letter-spacing: 0;
    white-space: nowrap;
  }

  &__hint {
    display: inline-block;
    margin-top: 6px;
    padding: 3px 10px;
    background: rgba(13, 13, 13, 0.18);
    color: rgba(13, 13, 13, 0.85);
    font-size: 11px;
    font-weight: 600;
    border-radius: 999px;
    border: 1px solid rgba(13, 13, 13, 0.12);
  }

  &__icon {
    position: relative;
    z-index: 1;
    color: rgba(0, 0, 0, 0.2);
  }

  &__mascot {
    position: relative;
    z-index: 1;
    width: 96px;
    height: 96px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    // Mascot "halo" — soft glow behind the character
    &::before {
      content: "";
      position: absolute;
      inset: -8px;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.35),
        transparent 60%
      );
      border-radius: 50%;
      animation: haloPulse 3s ease-in-out infinite;
      z-index: 0;
    }

    img {
      position: relative;
      z-index: 1;
      width: 96px;
      height: 96px;
      object-fit: contain;
      filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.5))
        drop-shadow(0 0 10px rgba(255, 217, 196, 0.4));
    }

    .mascot-shadow {
      position: absolute;
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      width: 64px;
      height: 10px;
      background: radial-gradient(
        ellipse,
        rgba(0, 0, 0, 0.55),
        transparent 70%
      );
      border-radius: 50%;
      animation: shadowPulse 4s ease-in-out infinite;
      z-index: 0;
    }
  }
}

@keyframes haloPulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.95);
  }
  50% {
    opacity: 0.65;
    transform: scale(1.05);
  }
}

@keyframes shadowPulse {
  0%,
  100% {
    transform: translateX(-50%) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translateX(-50%) scale(0.7);
    opacity: 0.4;
  }
}

// -----------------------------------------------------------------------------
// QUICK ACTIONS
// -----------------------------------------------------------------------------

.quick-actions {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 6px 16px;
  background: radial-gradient(
      ellipse at 50% 0%,
      rgba(255, 145, 101, 0.1),
      transparent 60%
    ),
    linear-gradient(145deg, #232323 0%, #121212 100%);
  border: 1.5px solid rgba(255, 145, 101, 0.18);
  border-radius: 18px;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.2s ease;
  cursor: pointer;
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.6), 0 8px 14px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 217, 196, 0.08);

  &:hover {
    border-color: rgba(255, 145, 101, 0.4);
  }

  &:active {
    transform: translateY(3px);
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.6), 0 3px 6px rgba(0, 0, 0, 0.4);
  }

  &__icon {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    color: #fff;
    box-shadow: 0 3px 0 rgba(0, 0, 0, 0.35),
      inset 0 2px 0 rgba(255, 255, 255, 0.22),
      inset 0 -2px 0 rgba(0, 0, 0, 0.18);
  }

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }

  // Variants
  &--deposit .action-btn__icon {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  }

  &--withdraw .action-btn__icon {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  }

  &--play .action-btn__icon {
    background: linear-gradient(135deg, #ff6b35 0%, #e55520 100%);
  }

  &--history .action-btn__icon {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  }
}

// -----------------------------------------------------------------------------
// SHARE BUTTON
// -----------------------------------------------------------------------------

.promo-banner--vipspin {
  border-color: rgba(240, 198, 90, 0.55);
  background: linear-gradient(135deg, #3a2606 0%, #6b4a06 55%, #c98f16 100%);
  box-shadow: 0 10px 28px rgba(107, 74, 6, 0.4);
}

.promo-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  margin-bottom: 16px;
  color: #fff;
  text-align: left;
  border: 1px solid rgba(255, 179, 71, 0.45);
  border-radius: 14px;
  background: linear-gradient(135deg, #7a1c45 0%, #b8285e 55%, #ff6b9d 100%);
  box-shadow: 0 10px 28px rgba(184, 40, 94, 0.35);
  cursor: pointer;

  &__emoji {
    font-size: 26px;
    line-height: 1;
  }
  &__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;

    strong {
      font-size: 15px;
      font-weight: 800;
    }
    small {
      font-size: 11px;
      opacity: 0.85;
    }
  }
  &__arrow {
    font-size: 26px;
    line-height: 1;
    opacity: 0.8;
  }
  &:active {
    transform: translateY(1px) scale(0.995);
  }
}

.share-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px 24px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #ff9165 0%, #ff6b35 50%, #e55520 100%);
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #0d0d0d;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(255, 107, 53, 0.4);

  svg {
    flex-shrink: 0;
  }

  &:active {
    transform: translateY(2px);
    box-shadow: 0 2px 10px rgba(255, 107, 53, 0.3);
  }
}

// -----------------------------------------------------------------------------
// ANNOUNCEMENTS
// -----------------------------------------------------------------------------

.announcements {
  margin-bottom: 24px;
}

.section-header {
  margin-bottom: 12px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Mali", "Kanit", sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #ff6b35;
  margin: 0;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);

  svg {
    flex-shrink: 0;
  }
}

.announcement-card {
  position: relative;
  background: radial-gradient(
      ellipse at 100% 0%,
      rgba(255, 145, 101, 0.08),
      transparent 50%
    ),
    linear-gradient(145deg, #1e1e1e 0%, #121212 100%);
  border: 1.5px solid rgba(255, 145, 101, 0.22);
  border-radius: 20px;
  padding: 16px 18px;
  box-shadow: 0 6px 0 rgba(0, 0, 0, 0.5), 0 12px 24px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 217, 196, 0.08);

  // Top "pinned banner" decoration
  &::before {
    content: "";
    position: absolute;
    top: -6px;
    left: 18px;
    width: 36px;
    height: 6px;
    background: linear-gradient(180deg, #ff9165, #ff6b35);
    border-radius: 0 0 6px 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  }
  &::after {
    content: "";
    position: absolute;
    top: -6px;
    right: 18px;
    width: 36px;
    height: 6px;
    background: linear-gradient(180deg, #ff9165, #ff6b35);
    border-radius: 0 0 6px 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  }
}

.announcement-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.announcement-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.announcement-icon {
  flex-shrink: 0;
  font-size: 14px;
}

// -----------------------------------------------------------------------------
// ANIMATIONS (from design system)
// -----------------------------------------------------------------------------

.animate-fade-in-up {
  animation: fadeInUp 0.5s ease-out forwards;
}

.animate-pulse-gold {
  animation: goldPulse 2s ease-in-out infinite;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes goldPulse {
  0%,
  100% {
    box-shadow: 0 4px 20px rgba(255, 107, 53, 0.4);
  }
  50% {
    box-shadow: 0 4px 30px rgba(255, 107, 53, 0.6);
  }
}
</style>
